import { useState, useEffect } from 'react'
import { getDataAPI, postDataAPI } from '../lib/api'
import Button from '../components/ui/Button'
import Modal from '../components/ui/Modal'
import DatePicker from '../components/ui/DatePicker'

interface Order {
  id: string
  order_number: string
  order_date: string
  total: number
  status: string
}

function getToday() {
  return new Date().toISOString().split('T')[0]
}

function getWeekStart() {
  const today = new Date()
  const day = today.getDay()
  const diff = today.getDate() - day
  return new Date(today.setDate(diff)).toISOString().split('T')[0]
}

function getMonthStart() {
  const today = new Date()
  return new Date(today.getFullYear(), today.getMonth(), 1).toISOString().split('T')[0]
}

export default function CommercePage() {
  const [orders, setOrders] = useState<Order[]>([])
  const [_isLoading, setIsLoading] = useState(true)
  const [isAddOpen, setIsAddOpen] = useState(false)
  const [isSubmitting, setIsSubmitting] = useState(false)
  const [formData, setFormData] = useState({
    order_number: '',
    total: 0,
    status: '',
    order_date: new Date().toISOString().split('T')[0],
  })

  useEffect(() => {
    const fetchOrders = async () => {
      try {
        const data = await getDataAPI('orders')
        setOrders(data || [])
      } catch (error) {
        console.error('Error fetching orders:', error)
      } finally {
        setIsLoading(false)
      }
    }
    fetchOrders()
  }, [])

  const refresh = async () => {
    const data = await getDataAPI('orders').catch(() => [])
    setOrders(data || [])
  }

  const handleCreate = async (e: React.FormEvent) => {
    e.preventDefault()
    setIsSubmitting(true)
    try {
      await postDataAPI('orders', formData)
      setIsAddOpen(false)
      await refresh()
    } catch (error) {
      console.error('Failed to create order:', error)
      alert('Failed to create order. Please try again.')
    } finally {
      setIsSubmitting(false)
    }
  }

  const today = getToday()
  const weekStart = getWeekStart()
  const monthStart = getMonthStart()
  const endDate = new Date().toISOString().split('T')[0]

  const ordersToday = orders.filter(o => o.order_date === today)
  const ordersWeek = orders.filter(o => {
    const orderDate = o.order_date
    return orderDate >= weekStart && orderDate <= endDate
  })
  const ordersMonth = orders.filter(o => {
    const orderDate = o.order_date
    return orderDate >= monthStart && orderDate <= endDate
  })

  const revenueToday = ordersToday.reduce((sum, o) => sum + o.total, 0)
  const revenueWeek = ordersWeek.reduce((sum, o) => sum + o.total, 0)
  const revenueMonth = ordersMonth.reduce((sum, o) => sum + o.total, 0)

  return (
    <div className="p-8 max-w-6xl mx-auto">
      <div className="mb-10 flex justify-between items-start">
        <div>
          <h1 className="text-2xl font-semibold tracking-tight mb-2">Fashion / Commerce</h1>
          <p className="text-sm text-gray-500">Simple commerce visibility</p>
        </div>
        <Button onClick={() => setIsAddOpen(true)}>Add Order</Button>
      </div>

      <Modal
        isOpen={isAddOpen}
        onClose={() => setIsAddOpen(false)}
        title="Add Order"
        maxWidthClassName="max-w-md"
      >
        <form onSubmit={handleCreate} className="space-y-5">
          <div>
            <label className="block text-sm font-medium mb-2">Order Number</label>
            <input
              type="text"
              required
              value={formData.order_number}
              onChange={(e) => setFormData({ ...formData, order_number: e.target.value })}
              className="ds-input"
            />
          </div>
          <div>
            <label className="block text-sm font-medium mb-2">Total</label>
            <input
              type="number"
              step="0.01"
              required
              value={formData.total}
              onChange={(e) => setFormData({ ...formData, total: parseFloat(e.target.value) || 0 })}
              className="ds-input"
            />
          </div>
          <div>
            <label className="block text-sm font-medium mb-2">Status</label>
            <input
              type="text"
              required
              value={formData.status}
              onChange={(e) => setFormData({ ...formData, status: e.target.value })}
              className="ds-input"
            />
          </div>
          <div>
            <DatePicker
              label="Order Date"
              value={formData.order_date}
              onChange={(value) => setFormData({ ...formData, order_date: value })}
              required
            />
          </div>
          <div className="flex gap-3 pt-4 border-t border-gray-200">
            <Button type="submit" disabled={isSubmitting} className="flex-1">
              {isSubmitting ? 'Saving…' : 'Save'}
            </Button>
            <Button type="button" variant="secondary" className="flex-1" onClick={() => setIsAddOpen(false)}>
              Cancel
            </Button>
          </div>
        </form>
      </Modal>

      {/* Summary Stats */}
      <div className="mb-10 grid grid-cols-1 md:grid-cols-3 gap-4">
        <div className="bg-white rounded-lg border border-gray-200 p-5">
          <div className="text-xs text-gray-500 mb-2 uppercase tracking-wider">Orders Today</div>
          <div className="text-2xl font-semibold text-gray-900 mb-1">{ordersToday.length}</div>
          <div className="text-xs text-gray-500">Revenue: ${revenueToday.toLocaleString(undefined, { minimumFractionDigits: 2, maximumFractionDigits: 2 })}</div>
        </div>
        <div className="bg-white rounded-lg border border-gray-200 p-5">
          <div className="text-xs text-gray-500 mb-2 uppercase tracking-wider">Orders This Week</div>
          <div className="text-2xl font-semibold text-gray-900 mb-1">{ordersWeek.length}</div>
          <div className="text-xs text-gray-500">Revenue: ${revenueWeek.toLocaleString(undefined, { minimumFractionDigits: 2, maximumFractionDigits: 2 })}</div>
        </div>
        <div className="bg-white rounded-lg border border-gray-200 p-5">
          <div className="text-xs text-gray-500 mb-2 uppercase tracking-wider">Orders This Month</div>
          <div className="text-2xl font-semibold text-gray-900 mb-1">{ordersMonth.length}</div>
          <div className="text-xs text-gray-500">Revenue: ${revenueMonth.toLocaleString(undefined, { minimumFractionDigits: 2, maximumFractionDigits: 2 })}</div>
        </div>
      </div>

      {/* Recent Orders */}
      <div className="mb-10">
        <h2 className="text-sm font-medium mb-4 text-gray-900">Recent Orders</h2>
        {orders.length > 0 ? (
          <div className="bg-white rounded-lg border border-gray-200 overflow-hidden">
            <table className="w-full border-collapse">
              <thead>
                <tr className="border-b border-gray-100 bg-gray-50/50">
                  <th className="text-left py-3 px-4 text-xs font-medium text-gray-700 uppercase tracking-wider">Order Number</th>
                  <th className="text-left py-3 px-4 text-xs font-medium text-gray-700 uppercase tracking-wider">Date</th>
                  <th className="text-right py-3 px-4 text-xs font-medium text-gray-700 uppercase tracking-wider">Total</th>
                  <th className="text-left py-3 px-4 text-xs font-medium text-gray-700 uppercase tracking-wider">Status</th>
                </tr>
              </thead>
              <tbody className="divide-y divide-gray-100">
                {orders.slice(0, 50).map((order) => (
                  <tr key={order.id} className="hover:bg-gray-50/50 transition-colors">
                    <td className="py-3 px-4 text-sm font-medium text-gray-900">{order.order_number}</td>
                    <td className="py-3 px-4 text-sm text-gray-600">{new Date(order.order_date).toLocaleDateString()}</td>
                    <td className="py-3 px-4 text-sm text-right text-gray-900 font-medium">${order.total.toLocaleString(undefined, { minimumFractionDigits: 2, maximumFractionDigits: 2 })}</td>
                    <td className="py-3 px-4">
                      <span className="inline-flex items-center px-2 py-1 rounded-full text-xs font-medium bg-gray-50 text-gray-700">
                        {order.status}
                      </span>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        ) : (
          <div className="bg-white rounded-lg border border-gray-200 p-12 text-center">
            <p className="text-sm text-gray-500">No orders found</p>
          </div>
        )}
      </div>
    </div>
  )
}
